/* MOTURO store behaviour · 0.16.0
   Vehicle state (header strip, header tool, collection/search vehicle context, fitment panels, mobile buy bar),
   product gallery, variant synchronisation, quantity steppers, section navigation and cart quantity updates.
   Rules: the stored vehicle is only ever shown as a label; every fitment state comes from
   MoturoVehicle.fitment(), which never returns "Passt" without verified records. Purchase availability is read
   from the option data written by Liquid (GPSR, staging, price and inventory gates) and never derived here.
   0.14.0: the single header vehicle entry (tool, strip action, PDP fitment links) opens and focuses the vehicle
   disclosure of the listing block already on the page instead of leaving to a second finder; only without such a
   block does it navigate to the canonical finder, carrying the article query of the search page and the real
   Warengruppe of a collection page as explicit parameters. Same-origin store routes only.
   2026-10-05 (ux-gallery): the product gallery lives in moturo-gallery.js (MoturoGallery); a variant change hands the
   featured image identity to the gallery through the DOM event moturo:gallery:show. The compact header language
   select submits its native form on change (bindLocalization); keyboard changes wait for Enter or the fallback button.
   2026-10-05 (safety-fitment, Stage B): every [data-fitment] element is rendered from the shared product context of
   MoturoVehicle.fitment() with its bound attributes (data-sku, data-handle, data-usage, data-usage-sku): panels get
   kicker/title/vehicle/reason and their two actions (recorded list, question about the fit), compact lines (.card-fit:
   cards and cart lines) get the card texts, the buy bar the short text of the first panel. [data-fitment-recorded]
   elements (lede, fitment warning) are shown only while recorded source relations exist for their article. Axes are
   exposed as data-fitment-state / -relation / -usage. No vehicle prompt is written for articles without recorded
   2026-10-06 (universal-accordion): the kicker badge is hidden while the statement carries no kicker (universal
   variant A shows "Universal-Ausführung" once as the title) and the contact action takes its state-dependent label
   (contactLabel). #product-safety is a native <details> in the service sidebar: links to it open the disclosure before
   the hash jump, a repeated click on an already set hash scrolls again, a direct load with the hash opens it.
   relations, and a variant switch rebinds data-sku, so a proof bound to another article number never carries over. */
(function () {
  'use strict';
  /* Locale strings: script#MoturoI18n (layout) holds the active theme locale, rendered with the native t filter and
     serialised with the json filter. The German literal is the base-locale (de.default) wording and is only used when
     no dictionary is on the page (offline tests); placeholders {{ name }} are filled as plain text. */
  const I18N = (() => { try { const el = typeof document !== 'undefined' && document.getElementById('MoturoI18n'); const d = el ? JSON.parse(el.textContent) : null; return d && typeof d === 'object' ? d : {}; } catch (_) { return {}; } })();
  const L = (name, base, vars) => { let s = typeof I18N[name] === 'string' ? I18N[name] : base; if (vars) for (const n of Object.keys(vars)) s = s.split('{{ ' + n + ' }}').join(String(vars[n])); return s; };
  const q = (selector, root) => Array.prototype.slice.call((root || document).querySelectorAll(selector));
  const setText = (root, selector, value) => q(selector, root).forEach(e => { e.textContent = value; });

  function shortLabel(vehicle) {
    if (!vehicle || typeof vehicle.label !== 'string') return L('vehicle_mine', 'Mein Fahrzeug');
    const designation = vehicle.label.split(' · ')[0].trim() || vehicle.label;
    return designation + (vehicle.year ? ' ' + vehicle.year : '');
  }

  function refresh() {
    const M = window.MoturoVehicle;
    if (!M) return;
    const vehicle = M.current(), has = !!vehicle, label = M.label(vehicle);
    setText(document, '[data-vehicle-label]', label);
    setText(document, '[data-vehicle-name]', label);
    setText(document, '[data-vehicle-short]', shortLabel(vehicle));
    q('[data-vehicle-tool]').forEach(e => e.setAttribute('aria-label', has ? L('vehicle_mine_label', 'Mein Fahrzeug: {{ label }}', { label }) : L('vehicle_mine', 'Mein Fahrzeug')));
    q('[data-vehicle-state]').forEach(e => e.setAttribute('data-vehicle-state', has ? 'set' : 'none'));
    q('[data-vehicle-clear]').forEach(e => { e.hidden = !has; });
    setText(document, '[data-vehicle-action]', has ? L('vehicle_change', 'Fahrzeug ändern') : L('vehicle_choose', 'Fahrzeug wählen'));
    setText(document, '[data-vehicle-action-long]', has ? L('vehicle_choose_other', 'Anderes Fahrzeug wählen') : L('vehicle_choose', 'Fahrzeug wählen'));
    let first = null;
    q('[data-fitment]').forEach(e => {
      const c = M.contextOf(e), f = M.fitment(vehicle, c.sku, [], c.ctx), compact = e.classList.contains('card-fit');
      if (!first && !compact) first = f;
      e.dataset.fitmentState = f.state;
      e.dataset.fitmentRelation = f.relation;
      e.dataset.fitmentUsage = f.usage;
      e.style.setProperty('--fit', f.color);
      e.style.borderLeftColor = f.color;
      setText(e, '[data-fitment-title]', compact ? f.card.title : f.title);
      setText(e, '[data-fitment-reason]', compact ? f.card.why : f.why);
      q('[data-fitment-kicker]', e).forEach(x => { x.textContent = f.kicker; x.hidden = !f.kicker; });
      setText(e, '[data-fitment-glyph]', f.glyph);
      q('[data-fitment-vehicle]', e).forEach(x => { x.textContent = f.vehicle; x.hidden = !f.vehicle; });
      q('[data-fitment-list]', e).forEach(x => { x.hidden = !f.list; });
      q('[data-fitment-contact]', e).forEach(x => { x.hidden = !f.contact; if (f.contactLabel) x.textContent = f.contactLabel; });
    });
    q('[data-fitment-recorded]').forEach(e => { e.hidden = M.relation(e.dataset.sku || '', e.dataset.handle || '').state !== 'recorded'; });
    if (first) setText(document, '[data-fitment-short]', first.short);
  }

  function bindVariant(select) {
    const apply = () => {
      const option = select.selectedOptions[0];
      if (!option) return;
      const ok = option.dataset.available === 'true';
      const availability = option.dataset.availability || (ok ? L('available', 'Verfügbar') : L('unavailable', 'Aktuell nicht verfügbar'));
      /* Language-neutral state from Liquid (data-availability-state); the German text comparison stays only as fallback for options rendered before 0.16. */
      const state = ok ? 'ok' : ((option.dataset.availabilityState || (availability === 'Noch nicht bestellbar' ? 'pending' : 'no')) === 'pending' ? 'pending' : 'no');
      setText(document, '[data-variant-price]', option.dataset.price || L('price_pending', 'Preis folgt'));
      q('[data-variant-availability]').forEach(e => { e.textContent = availability; e.dataset.availabilityState = state; });
      if (option.dataset.title) setText(document, '[data-variant-title]', option.dataset.title);
      const fact = (selector, value, mono) => q(selector).forEach(e => {
        const span = document.createElement('span');
        if (value) { if (mono) span.className = 'mono'; span.textContent = value; }
        else { span.textContent = ''; }
        const row=e.closest('[data-variant-fact-row]'); if(row)row.hidden=!value;
        e.replaceChildren(span);
      });
      fact('[data-variant-barcode]', option.dataset.barcode || '', true);
      fact('[data-variant-weight]', option.dataset.weight || '', false);
      q('[data-cart-submit]').forEach(e => { e.disabled = !ok; e.textContent = ok ? L('add_to_cart', 'In den Warenkorb') : (state === 'pending' ? L('not_orderable', 'Noch nicht bestellbar') : L('cta_unavailable', 'Nicht verfügbar')); });
      q('[data-fitment],[data-fitment-recorded]').forEach(e => { e.dataset.sku = option.dataset.sku || ''; });
      if (option.dataset.imageId || option.dataset.image) {
        /* The gallery of this product block shows the variant image by identity (moturo-gallery.js); without the gallery
           asset the main image is swapped directly and its srcset dropped so that no stale candidates remain. */
        const scope = select.closest('[data-product]') || document;
        const gallery = scope.querySelector('[data-gallery]');
        if (gallery && window.MoturoGallery) gallery.dispatchEvent(new CustomEvent('moturo:gallery:show', { detail: { id: option.dataset.imageId || '', src: option.dataset.image || '' } }));
        else if (option.dataset.image) {
          const img = scope.querySelector('.product-photo img');
          if (img) { img.src = option.dataset.image; img.removeAttribute('srcset'); img.removeAttribute('sizes'); }
        }
      }
      try { const url = new URL(window.location.href); url.searchParams.set('variant', option.value); window.history.replaceState(null, '', url); } catch (_) {}
      refresh();
    };
    select.addEventListener('change', apply);
  }

  function bindSectionNav(nav) {
    const links = q('a[href^="#"]', nav);
    const targets = links.map(a => document.getElementById(a.getAttribute('href').slice(1))).filter(Boolean);
    if (!targets.length) return;
    const setCurrent = id => links.forEach(a => { if (a.getAttribute('href') === '#' + id) a.setAttribute('aria-current', 'true'); else a.removeAttribute('aria-current'); });
    let ticking = false, clicked = 0;
    const update = () => {
      if (Date.now() - clicked < 800) return;
      const line = nav.getBoundingClientRect().bottom + 24;
      const flowing = targets.filter(t => window.getComputedStyle(t).position !== 'sticky');
      let current = flowing[0] || targets[0];
      for (const t of flowing) if (t.getBoundingClientRect().top <= line) current = t;
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2) current = flowing[flowing.length - 1] || current;
      setCurrent(current.id);
    };
    window.addEventListener('scroll', () => { if (!ticking) { ticking = true; window.requestAnimationFrame(() => { update(); ticking = false; }); } }, { passive: true });
    links.forEach(a => a.addEventListener('click', () => { clicked = Date.now(); setCurrent(a.getAttribute('href').slice(1)); }));
    update();
  }

  function stepQuantity(button) {
    const input = document.getElementById(button.getAttribute('aria-controls') || '');
    if (!input) return;
    const min = input.min === '' ? 0 : Number(input.min), step = Number(button.dataset.qtyStep) || 1;
    let value = parseInt(input.value, 10);
    if (!Number.isFinite(value)) value = min;
    value = Math.max(min, value + step);
    if (input.max !== '') value = Math.min(Number(input.max), value);
    input.value = String(value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  let cartTimer;
  function bindCartLine(input) {
    input.addEventListener('change', () => {
      const form = input.form;
      if (!form || !form.classList.contains('cart-layout')) return;
      clearTimeout(cartTimer);
      cartTimer = setTimeout(() => {
        const update = form.querySelector('button[name="update"]');
        if (typeof form.requestSubmit === 'function') form.requestSubmit(update || undefined); else form.submit();
      }, 400);
    });
  }

  function bindNavGroup(group) {
    const summary = group.querySelector('summary');
    group.addEventListener('toggle', () => { if (group.open) q('.nav-group').forEach(other => { if (other !== group) other.open = false; }); });
    group.addEventListener('keydown', event => { if (event.key === 'Escape' && group.open) { group.open = false; if (summary) summary.focus(); } });
    document.addEventListener('click', event => { if (group.open && !group.contains(event.target)) group.open = false; });
  }

  function bind() {
    q('.nav-group').forEach(e => { if (!e.dataset.bound) { e.dataset.bound = 'true'; bindNavGroup(e); } });
    q('#HeaderLocalization').forEach(e => { if (!e.dataset.bound) { e.dataset.bound = 'true'; bindLocalization(e); } });
    q('[data-variant-select]').forEach(e => { if (!e.dataset.bound) { e.dataset.bound = 'true'; bindVariant(e); } });
    if (location.hash === SAFETY_HASH) openSafetyDisclosure(true);
    q('.product-nav').forEach(e => { if (!e.dataset.bound) { e.dataset.bound = 'true'; bindSectionNav(e); } });
    q('input[name="updates[]"]').forEach(e => { if (!e.dataset.bound) { e.dataset.bound = 'true'; bindCartLine(e); } });
    refresh();
  }

  /* The vehicle disclosure of a mounted, visible listing block on this page (moturo-catalogue.js), if any. */
  function vehiclePanel() {
    return q('[data-catalogue-vehicle-panel]').find(panel => {
      const ui = panel.closest('[data-editor-catalogue-ui]');
      return !!ui && ui.dataset.catalogueState === 'ready' && !ui.hidden && panel.isConnected;
    }) || null;
  }
  function openVehiclePanel(change) {
    const panel = vehiclePanel();
    if (!panel) return false;
    panel.open = true;
    const changeButton = panel.querySelector('[data-catalogue-vehicle-change]');
    const changeVisible = changeButton && !changeButton.hidden && !changeButton.closest('[hidden]');
    if (change && changeVisible) changeButton.click();
    else {
      const first = panel.querySelector('[data-catalogue-make]'), summary = panel.querySelector('summary');
      if (first && !first.disabled && !first.closest('[hidden]')) first.focus(); else if (summary) summary.focus();
    }
    panel.scrollIntoView({ block: 'start' });
    return true;
  }
  /* Canonical finder route with the article context of this page: q from the search page, the real Warengruppe
     of a collection page, Shopify preview parameters. Never a foreign origin, never an arbitrary redirect. */
  function finderHref(link) {
    try {
      const url = new URL(link.getAttribute('href') || '', location.href);
      if (url.origin !== location.origin) return null;
      const params = new URLSearchParams(location.search);
      const hasContext = !!document.querySelector('[data-catalogue-context]');
      const query = hasContext ? (params.get('q') || '') : '';
      const groupEl = document.querySelector('[data-catalogue-preset-group],[data-catalogue-context-group]');
      const known = new Set(q('.nav-submenu a').filter(a => {
        try { const u = new URL(a.href); return u.origin === location.origin && /\/collections\/[^/]+$/.test(u.pathname); } catch (_) { return false; }
      }).map(a => (a.dataset && a.dataset.group) || a.textContent.trim()));
      const wanted = hasContext ? params.get('group') || '' : '';
      const group = groupEl ? (groupEl.dataset.cataloguePresetGroup || groupEl.dataset.catalogueContextGroup || '') : known.has(wanted) ? wanted : '';
      if (query) url.searchParams.set('q', query);
      if (group) url.searchParams.set('group', group);
      for (const [k, v] of params) if (k === 'preview_theme_id' || k.startsWith('_')) url.searchParams.set(k, v);
      return url.href;
    } catch (_) { return null; }
  }
  /* Header localization (2026-10-05): #HeaderLocalization is Shopify's native localization form, which returns to the
     current path (hidden return_to). On submit the current query string (search terms, filters, variant, preview
     parameters) is appended so a language or country switch keeps the view. Same-origin relative paths only; a return_to
     that already carries a query or fragment stays untouched. Without JS the native form works as is: the plain submit
     button stays visible. With JS a pointer change of a select submits at once; after a keyboard change (arrow keys,
     type-ahead) the form waits for Enter on the select or for the fallback button, which is shown again for that case. */
  function bindLocalization(form) {
    const selects = q('select', form), submit = form.querySelector('[data-locale-submit]');
    if (!selects.length) return;
    const send = () => { if (typeof form.requestSubmit === 'function') form.requestSubmit(); else form.submit(); };
    const keyboardKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'];
    selects.forEach(select => {
      let keyboard = false;
      select.dataset.initial = select.value;
      select.addEventListener('pointerdown', () => { keyboard = false; });
      select.addEventListener('keydown', event => {
        if (event.key === 'Enter') { if (select.value !== select.dataset.initial) { event.preventDefault(); send(); } return; }
        if (keyboardKeys.includes(event.key) || (event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey)) keyboard = true;
      });
      select.addEventListener('change', () => {
        if (keyboard) { if (submit) submit.hidden = false; return; }
        send();
      });
    });
    if (submit) submit.hidden = true;
    form.dataset.autosubmit = 'true';
  }
  document.addEventListener('submit', event => {
    const form = event.target && event.target.closest ? event.target.closest('#HeaderLocalization') : null;
    if (!form || !location.search || location.search.length < 2) return;
    const back = form.querySelector('input[name="return_to"]');
    if (!back) return;
    const value = String(back.value || '');
    if (!value.startsWith('/') || value.startsWith('//') || value.includes('?') || value.includes('#')) return;
    back.value = value + location.search;
  }, true);
  /* 2026-10-06 (universal-accordion): #product-safety disclosure. Native <details>, initially closed, operable without
     script. With script every same-page link to #product-safety (buy links, product nav, safety notes) opens it before
     the browser performs the hash jump; when the hash is already set no hashchange fires, so the click scrolls itself. */
  const SAFETY_HASH = '#product-safety';
  function openSafetyDisclosure(scroll) {
    const el = document.getElementById('product-safety');
    if (!el || el.tagName !== 'DETAILS') return false;
    if (!el.open) el.open = true;
    if (scroll) requestAnimationFrame(() => { try { el.scrollIntoView({ block: 'start' }); } catch (_) { el.scrollIntoView(); } });
    return true;
  }
  function isSafetyLink(a) {
    let url;
    try { url = new URL(a.getAttribute('href') || '', location.href); } catch (_) { return false; }
    return url.hash === SAFETY_HASH && url.origin === location.origin && url.pathname === location.pathname && url.search === location.search;
  }
  document.addEventListener('click', event => {
    const clear = event.target.closest('[data-vehicle-clear]');
    if (clear && window.MoturoVehicle) { event.preventDefault(); window.MoturoVehicle.clear(); return; }
    const entry = event.target.closest('[data-vehicle-tool],[data-vehicle-action],[data-vehicle-action-long]');
    if (entry && window.MoturoVehicle && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      const change = !entry.hasAttribute('data-vehicle-tool') || !!window.MoturoVehicle.current();
      if (openVehiclePanel(change)) { event.preventDefault(); return; }
      const href = finderHref(entry);
      if (href && href !== entry.href) { event.preventDefault(); location.assign(href); }
      return;
    }
    const safety = event.target.closest('a[href]');
    if (safety && isSafetyLink(safety) && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
      const again = location.hash === SAFETY_HASH;
      if (openSafetyDisclosure(again) && again) event.preventDefault();
      return;
    }
    const step = event.target.closest('[data-qty-step]');
    if (step) { event.preventDefault(); stepQuantity(step); }
  });
  window.addEventListener('hashchange', () => { if (location.hash === SAFETY_HASH) openSafetyDisclosure(true); });
  window.addEventListener('moturo:vehicle', refresh);
  window.addEventListener('storage', event => { if (window.MoturoVehicle && event.key === window.MoturoVehicle.key) refresh(); });
  document.addEventListener('shopify:section:load', bind);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind); else bind();
})();
